import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useBiometric } from '@/hooks/useBiometric';
import { GradientBackground } from '@/components/ui/GradientBackground';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PlatformService } from '@/utils/platform';
import { redirectPersistenceService } from '@/services/redirectPersistence';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Fingerprint,
  Shield,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';

const MobileLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { isAvailable: biometricAvailable, authenticate: authenticateBiometric } = useBiometric();

  useEffect(() => {
    // Configure status bar for mobile
    if (PlatformService.isNative()) {
      StatusBar.setStyle({ style: Style.Light });
      if (PlatformService.isAndroid()) {
        StatusBar.setBackgroundColor({ color: '#667eea' });
      }
    }
  }, []);

  const hapticFeedback = async (style: ImpactStyle = ImpactStyle.Light) => {
    if (PlatformService.isNative()) {
      try {
        await Haptics.impact({ style });
      } catch (error) {
        console.log('Haptics not available');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      await hapticFeedback(ImpactStyle.Medium);
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    await hapticFeedback(ImpactStyle.Medium);

    try {
      await login(email, password);
      await hapticFeedback(ImpactStyle.Heavy);
      // Check for stored redirect URL (deep linking support)
      const redirectUrl = redirectPersistenceService.getAndClearRedirectUrl(useAuth.getState().user?.id);
      navigate(redirectUrl || '/dashboard');
    } catch (error) {
      await hapticFeedback(ImpactStyle.Heavy);
      // Error is handled by the auth hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    await hapticFeedback(ImpactStyle.Light);
    const result = await authenticateBiometric();

    if (result.success) {
      await hapticFeedback(ImpactStyle.Heavy);
      // Check for stored redirect URL (deep linking support)
      const redirectUrl = redirectPersistenceService.getAndClearRedirectUrl(useAuth.getState().user?.id);
      navigate(redirectUrl || '/dashboard');
    } else {
      await hapticFeedback(ImpactStyle.Medium);
      if (result.error) {
        toast.error(result.error);
      }
    }
  };

  const handleFieldFocus = async (fieldName: string) => {
    setFocusedField(fieldName);
    await hapticFeedback(ImpactStyle.Light);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const logoVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      rotate: 5,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  return (
    <GradientBackground>
      <div className="min-h-screen-safe flex flex-col items-center justify-center px-6 safe-top safe-bottom">
        <motion.div
          className="w-full max-w-md"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Floating Logo */}
          <motion.div
            className="flex flex-col items-center mb-12"
            variants={itemVariants}
          >
            <motion.div
              className="relative"
              variants={logoVariants}
              initial="initial"
              animate="animate"
              whileHover="hover"
            >
              {/* Glow effect */}
              <motion.div
                className="absolute inset-0 bg-white rounded-full blur-2xl opacity-30"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Logo container */}
              <motion.div
                className="relative w-24 h-24 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center border border-white/30 shadow-2xl"
                animate={floatingAnimation}
              >
                <Sparkles className="w-12 h-12 text-white" />
              </motion.div>
            </motion.div>

            <motion.h1
              className="mt-6 text-4xl font-bold text-white text-center"
              variants={itemVariants}
            >
              CareTracker
            </motion.h1>
            <motion.p
              className="mt-2 text-white/80 text-center text-sm"
              variants={itemVariants}
            >
              Medical Intelligence Platform
            </motion.p>
          </motion.div>

          {/* Login Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="space-y-5"
            variants={itemVariants}
          >
            {/* Email Input */}
            <motion.div
              className="relative"
              whileTap={{ scale: 0.98 }}
            >
              <div
                className={`relative backdrop-blur-xl bg-white/10 rounded-2xl border transition-all duration-300 ${
                  focusedField === 'email'
                    ? 'border-white/60 shadow-lg shadow-white/20'
                    : 'border-white/20'
                }`}
              >
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Mail className="w-5 h-5 text-white/60" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => handleFieldFocus('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Email address"
                  className="w-full pl-12 pr-4 py-4 bg-transparent text-white placeholder-white/50 focus:outline-none text-base"
                  autoComplete="email"
                  autoCapitalize="none"
                />
              </div>
            </motion.div>

            {/* Password Input */}
            <motion.div
              className="relative"
              whileTap={{ scale: 0.98 }}
            >
              <div
                className={`relative backdrop-blur-xl bg-white/10 rounded-2xl border transition-all duration-300 ${
                  focusedField === 'password'
                    ? 'border-white/60 shadow-lg shadow-white/20'
                    : 'border-white/20'
                }`}
              >
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Lock className="w-5 h-5 text-white/60" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => handleFieldFocus('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Password"
                  className="w-full pl-12 pr-12 py-4 bg-transparent text-white placeholder-white/50 focus:outline-none text-base"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={async () => {
                    setShowPassword(!showPassword);
                    await hapticFeedback(ImpactStyle.Light);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-white/60" />
                  ) : (
                    <Eye className="w-5 h-5 text-white/60" />
                  )}
                </button>
              </div>
            </motion.div>

            {/* Sign In Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="relative w-full py-4 bg-white text-purple-700 rounded-2xl font-semibold text-base overflow-hidden shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => hapticFeedback(ImpactStyle.Medium)}
            >
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                animate={{
                  x: ['-100%', '200%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
              />

              <span className="relative z-10 flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-2 border-purple-700 border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </span>
            </motion.button>
          </motion.form>

          {/* Biometric Login */}
          <AnimatePresence>
            {biometricAvailable && (
              <motion.div
                className="mt-6"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <span className="relative bg-transparent px-4 text-sm text-white/60">
                    or
                  </span>
                </div>

                <motion.button
                  type="button"
                  onClick={handleBiometricLogin}
                  className="mt-6 w-full py-4 backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl font-medium text-white flex items-center justify-center gap-3"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <Fingerprint className="w-6 h-6" />
                  </motion.div>
                  Sign in with Biometric
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Register Link */}
          <motion.div
            className="mt-8 text-center"
            variants={itemVariants}
          >
            <p className="text-white/80 text-sm">
              Need access? Contact your organization administrator.
            </p>
          </motion.div>

          {/* HIPAA Badge */}
          <motion.div
            className="mt-8 backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-4"
            variants={itemVariants}
          >
            <div className="flex items-center justify-center gap-3">
              <Shield className="w-5 h-5 text-white/80" />
              <p className="text-white/80 text-xs font-medium">
                HIPAA Compliant & Encrypted
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </GradientBackground>
  );
};

export default MobileLogin;
